/**
 * Time/date formatters. One home for the wall-clock, date, age, and duration
 * shapes the UI needs, so components stop hand-rolling divergent `fmtTime`
 * helpers. Each function is a distinct, intentional format — they are not
 * interchangeable.
 */

/** Wall-clock with millisecond precision, e.g. "09:36:04.812". Log scanning. */
export function formatClockPrecise(ms: number): string {
  const d = new Date(ms)
  const pad = (n: number, w = 2) => n.toString().padStart(w, '0')
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(
    d.getMilliseconds(),
    3,
  )}`
}

/** Locale date + time, e.g. "7/30/2026, 9:36:04 AM". Admin run rows. */
export function formatDateTime(ms: number): string {
  return new Date(ms).toLocaleString()
}

/** Locale date only, e.g. "7/30/2026". */
export function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString()
}

/** Coarse elapsed time rounded to one unit, e.g. "500ms" / "3s" / "5m". */
export function formatAge(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`
  return `${Math.round(ms / 60_000)}m`
}

/** Duration with minute+second precision, e.g. "500ms" / "1.5s" / "2m3s". */
export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`
  // Round to whole seconds first so 119.6s renders "2m0s", not "1m60s".
  const totalS = Math.round(ms / 1000)
  const m = Math.floor(totalS / 60)
  const s = totalS % 60
  return `${m}m${s}s`
}

/**
 * Coarse relative age of a past timestamp, e.g. "just now" / "3m ago" /
 * "5h ago" / "2d ago" / "8w ago". Falls back to a locale date past ~9 weeks,
 * where relative units get unhelpful. Used for prompt-history rows.
 *
 * Pass `now` (e.g. from `useNow`) so the caller can drive periodic refresh;
 * defaults to `Date.now()` for one-shot calls and tests.
 */
export function formatRelative(ms: number, now: number = Date.now()): string {
  const sec = Math.round((now - ms) / 1000)
  if (sec < 45) return 'just now'
  const min = Math.round(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.round(min / 60)
  if (hr < 24) return `${hr}h ago`
  const day = Math.round(hr / 24)
  if (day < 7) return `${day}d ago`
  const wk = Math.round(day / 7)
  if (wk <= 9) return `${wk}w ago`
  return formatDate(ms)
}
